import { useState, useEffect, useMemo } from 'react'
import { supabase } from '@/lib/supabase'
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts'
import {
  DollarSign,
  TrendingUp,
  UsersRound,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Filter,
  CreditCard,
  Clock
} from 'lucide-react'

interface Payment {
  id: string
  profile_id: string | null
  student_id: string | null
  amount: number
  currency: string
  status: string
  payment_method: string | null
  payment_date: string | null
  created_at: string
  start_date: string | null
  cohort_id: string | null
  schedule_day: string | null
}

interface StudentInfo {
  id: string
  profile_id: string
  payment_amount: number | null
  subscription_status: string
  enrollment_date: string
  program_start_date: string | null
}

interface ProfileInfo {
  id: string
  first_name: string
  last_name: string
  email: string
}

interface CohortInfo {
  id: string
  name: string
  student_ids: string[]
  start_date: string | null
  is_active: boolean
}

const COLORS = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#818cf8', '#7c3aed', '#4f46e5', '#4338ca']
const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  approved: { bg: 'bg-green-100', text: 'text-green-700', label: 'Aprobado' },
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pendiente' },
  cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelado' },
  rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rechazado' },
}

const formatCLP = (amount: number) =>
  `$${Math.round(amount).toLocaleString('es-CL')}`

export default function SalesDashboard() {
  const [payments, setPayments] = useState<Payment[]>([])
  const [students, setStudents] = useState<StudentInfo[]>([])
  const [profiles, setProfiles] = useState<ProfileInfo[]>([])
  const [cohorts, setCohorts] = useState<CohortInfo[]>([])
  const [loading, setLoading] = useState(true)

  // Filters
  const [selectedCohort, setSelectedCohort] = useState<string>('all')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  useEffect(() => {
    async function load() {
      setLoading(true)
      const [paymentsRes, studentsRes, profilesRes, cohortsRes] = await Promise.all([
        (supabase.from('payments') as any)
          .select('id, profile_id, student_id, amount, currency, status, payment_method, payment_date, created_at, start_date, cohort_id, schedule_day')
          .order('created_at', { ascending: false }),
        (supabase.from('students') as any)
          .select('id, profile_id, payment_amount, subscription_status, enrollment_date, program_start_date'),
        (supabase.from('profiles') as any)
          .select('id, first_name, last_name, email')
          .eq('role', 'student'),
        (supabase.from('cohorts') as any)
          .select('id, name, student_ids, start_date, is_active')
          .order('created_at', { ascending: false })
      ])

      if (paymentsRes.data) setPayments(paymentsRes.data)
      if (studentsRes.data) setStudents(studentsRes.data)
      if (profilesRes.data) setProfiles(profilesRes.data)
      if (cohortsRes.data) setCohorts(cohortsRes.data)
      setLoading(false)
    }
    load()
  }, [])

  // Maps
  const profileMap = useMemo(() => {
    const map = new Map<string, ProfileInfo>()
    profiles.forEach(p => map.set(p.id, p))
    return map
  }, [profiles])

  const cohortMap = useMemo(() => {
    const map = new Map<string, CohortInfo>()
    cohorts.forEach(c => map.set(c.id, c))
    return map
  }, [cohorts])

  const profileCohortMap = useMemo(() => {
    const map = new Map<string, string[]>()
    cohorts.forEach(c => {
      (c.student_ids || []).forEach((pid: string) => {
        const existing = map.get(pid) || []
        existing.push(c.name)
        map.set(pid, existing)
      })
    })
    return map
  }, [cohorts])

  // Filtered payments
  const filteredPayments = useMemo(() => {
    let result = payments

    if (selectedStatus !== 'all') {
      result = result.filter(p => p.status === selectedStatus)
    }

    if (selectedCohort !== 'all') {
      const cohort = cohorts.find(c => c.id === selectedCohort)
      if (cohort) {
        const profileIds = new Set(cohort.student_ids || [])
        result = result.filter(p => p.cohort_id === selectedCohort || (p.profile_id && profileIds.has(p.profile_id)))
      }
    }

    if (dateFrom) {
      result = result.filter(p => {
        const d = (p.payment_date || p.created_at).slice(0, 10)
        return d >= dateFrom
      })
    }
    if (dateTo) {
      result = result.filter(p => {
        const d = (p.payment_date || p.created_at).slice(0, 10)
        return d <= dateTo
      })
    }

    return result
  }, [payments, cohorts, selectedCohort, selectedStatus, dateFrom, dateTo])

  // === KPIs ===
  const approvedPayments = filteredPayments.filter(p => p.status === 'approved')
  const pendingPayments = filteredPayments.filter(p => p.status === 'pending')
  const totalRevenue = approvedPayments.reduce((sum, p) => sum + Number(p.amount), 0)
  const totalPending = pendingPayments.reduce((sum, p) => sum + Number(p.amount), 0)
  const avgTicket = approvedPayments.length > 0 ? totalRevenue / approvedPayments.length : 0
  const totalTransactions = filteredPayments.length
  const approvedCount = approvedPayments.length
  const approvalRate = totalTransactions > 0 ? (approvedCount / totalTransactions) * 100 : 0

  // Also count students created with payment_amount (manual entry)
  const manualPayments = students.filter(s => s.payment_amount && s.payment_amount > 0)
  const manualTotal = manualPayments.reduce((sum, s) => sum + (s.payment_amount || 0), 0)

  // === Revenue by month (from payments table) ===
  const monthlyData = useMemo(() => {
    const map = new Map<string, { month: string, aprobado: number, pendiente: number, transacciones: number }>()

    filteredPayments.forEach(p => {
      const date = p.payment_date || p.created_at
      if (!date) return
      const month = date.slice(0, 7)
      const existing = map.get(month) || { month, aprobado: 0, pendiente: 0, transacciones: 0 }
      const amount = Number(p.amount)
      if (p.status === 'approved') existing.aprobado += amount
      else if (p.status === 'pending') existing.pendiente += amount
      existing.transacciones += 1
      map.set(month, existing)
    })

    return Array.from(map.values()).sort((a, b) => a.month.localeCompare(b.month))
  }, [filteredPayments])

  // === Revenue by cohort ===
  const cohortData = useMemo(() => {
    // Group payments by cohort
    const cohortPayments = new Map<string, { revenue: number; pending: number; count: number; students: Set<string> }>()

    // Init from cohorts
    cohorts.forEach(c => {
      cohortPayments.set(c.id, { revenue: 0, pending: 0, count: 0, students: new Set(c.student_ids || []) })
    })

    // Add a "sin cohorte" bucket
    cohortPayments.set('none', { revenue: 0, pending: 0, count: 0, students: new Set() })

    approvedPayments.forEach(p => {
      const amount = Number(p.amount)
      let cohortId = p.cohort_id

      // If no cohort_id on payment, try to find via profile
      if (!cohortId && p.profile_id) {
        for (const c of cohorts) {
          if ((c.student_ids || []).includes(p.profile_id)) {
            cohortId = c.id
            break
          }
        }
      }

      const bucket = cohortPayments.get(cohortId || 'none') || cohortPayments.get('none')!
      bucket.revenue += amount
      bucket.count += 1
      if (p.profile_id) bucket.students.add(p.profile_id)
    })

    return Array.from(cohortPayments.entries())
      .map(([id, data]) => ({
        id,
        name: id === 'none' ? 'Sin cohorte' : cohortMap.get(id)?.name || id,
        revenue: data.revenue,
        pending: data.pending,
        transactions: data.count,
        students: data.students.size,
        active: id === 'none' ? false : cohortMap.get(id)?.is_active || false
      }))
      .filter(c => c.transactions > 0 || c.students > 0)
      .sort((a, b) => b.revenue - a.revenue)
  }, [cohorts, cohortMap, approvedPayments])

  // === Status distribution ===
  const statusData = useMemo(() => {
    const map = new Map<string, number>()
    filteredPayments.forEach(p => {
      map.set(p.status, (map.get(p.status) || 0) + 1)
    })
    return Array.from(map.entries()).map(([status, value]) => ({
      name: STATUS_COLORS[status]?.label || status,
      value
    }))
  }, [filteredPayments])

  // === Payment method distribution ===
  const methodData = useMemo(() => {
    const map = new Map<string, { count: number; revenue: number }>()
    approvedPayments.forEach(p => {
      const method = p.payment_method || 'No especificado'
      const existing = map.get(method) || { count: 0, revenue: 0 }
      existing.count += 1
      existing.revenue += Number(p.amount)
      map.set(method, existing)
    })
    return Array.from(map.entries()).map(([name, data]) => ({
      name: name === 'webpay_plus' ? 'Webpay Plus' : name === 'mercadopago' ? 'MercadoPago' : name,
      cantidad: data.count,
      ingresos: data.revenue
    }))
  }, [approvedPayments])

  // Format month
  const formatMonth = (monthStr: string) => {
    const [y, m] = monthStr.split('-')
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
    return `${months[Number.parseInt(m) - 1]} ${y.slice(2)}`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <DollarSign className="w-7 h-7 text-green-600" />
          Panel de Ventas
        </h1>
        <p className="text-gray-600 mt-1">
          {payments.length} transacciones registradas &middot; {profiles.length} alumnos
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filtros</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label htmlFor="filter-cohort"className="block text-xs font-medium text-gray-500 mb-1">Cohorte</label>
            <select id="filter-cohort" value={selectedCohort}
              onChange={e => setSelectedCohort(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">Todas las cohortes</option>
              {cohorts.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name} ({(c.student_ids || []).length})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="filter-status" className="block text-xs font-medium text-gray-500 mb-1">Estado</label>
            <select id="filter-status" value={selectedStatus}
              onChange={e => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">Todos</option>
              <option value="approved">Aprobados</option>
              <option value="pending">Pendientes</option>
              <option value="cancelled">Cancelados</option>
            </select>
          </div>
          <div>
            <label htmlFor="filter-date-from" className="block text-xs font-medium text-gray-500 mb-1">Desde</label>
            <input
              type="date"
              id="filter-date-from"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="filter-date-to" className="block text-xs font-medium text-gray-500 mb-1">Hasta</label>
            <input
              type="date"
              id="filter-date-to"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
        {(selectedCohort !== 'all' || selectedStatus !== 'all' || dateFrom || dateTo) && (
          <button
            onClick={() => { setSelectedCohort('all'); setSelectedStatus('all'); setDateFrom(''); setDateTo('') }}
            className="mt-3 text-xs text-indigo-600 hover:text-indigo-800"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <span className="flex items-center gap-0.5 text-xs font-medium text-green-600">
              <ArrowUpRight className="w-3 h-3" />
              Aprobado
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatCLP(totalRevenue)}</p>
          <p className="text-xs text-gray-500 mt-1">Ingresos aprobados</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatCLP(totalPending)}</p>
          <p className="text-xs text-gray-500 mt-1">Pendientes</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatCLP(avgTicket)}</p>
          <p className="text-xs text-gray-500 mt-1">Ticket promedio</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-[#0055A4]/10 rounded-lg flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-[#0055A4]" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {approvedCount}<span className="text-lg text-gray-400">/{totalTransactions}</span>
          </p>
          <p className="text-xs text-gray-500 mt-1">Transacciones aprobadas</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <UsersRound className="w-5 h-5 text-purple-600" />
            </div>
            {approvalRate > 0 && (
              <span className={`flex items-center gap-0.5 text-xs font-medium ${approvalRate >= 50 ? 'text-green-600' : 'text-amber-600'}`}>
                {approvalRate >= 50 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {approvalRate.toFixed(0)}%
              </span>
            )}
          </div>
          <p className="text-2xl font-bold text-gray-900">{approvalRate.toFixed(1)}%</p>
          <p className="text-xs text-gray-500 mt-1">Tasa de aprobacion</p>
        </div>
      </div>

      {/* Manual payments note */}
      {manualTotal > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <DollarSign className="w-5 h-5 text-amber-600 shrink-0" />
          <div className="text-sm text-amber-800">
            Además hay <strong>{formatCLP(manualTotal)}</strong> registrados manualmente en {manualPayments.length} alumno{manualPayments.length !== 1 ? 's' : ''} (campo monto en creación).
            <span className="text-amber-600 ml-1">Estos no se incluyen en los graficos de transacciones.</span>
          </div>
        </div>
      )}

      {/* Charts row 1 */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Revenue over time */}
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Ingresos por mes</h3>
          {monthlyData.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
              Sin datos de ingresos
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tickFormatter={formatMonth} fontSize={12} />
                <YAxis tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} fontSize={12} />
                <Tooltip
                  formatter={((value: any, name: any) => [formatCLP(value), name === 'aprobado' ? 'Aprobado' : 'Pendiente']) as any}
                  labelFormatter={formatMonth as any}
                />
                <Bar dataKey="aprobado" fill="#22c55e" radius={[4, 4, 0, 0]} name="Aprobado" stackId="a" />
                <Bar dataKey="pendiente" fill="#eab308" radius={[4, 4, 0, 0]} name="Pendiente" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Transactions over time */}
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Transacciones por mes</h3>
          {monthlyData.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
              Sin datos
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tickFormatter={formatMonth} fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip
                  formatter={((value: any) => [value, 'Transacciones']) as any}
                  labelFormatter={formatMonth as any}
                />
                <Line
                  type="monotone"
                  dataKey="transacciones"
                  stroke="#6366f1"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Transacciones"
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Charts row 2 */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Revenue by cohort */}
        <div className="bg-white rounded-xl shadow-sm border p-5 lg:col-span-2">
          <h3 className="font-semibold text-gray-900 mb-4">Ingresos por cohorte</h3>
          {cohortData.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
              Sin datos por cohorte
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(200, cohortData.length * 50)}>
              <BarChart data={cohortData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} fontSize={12} />
                <YAxis type="category" dataKey="name" width={140} fontSize={12} />
                <Tooltip formatter={((value: any) => [formatCLP(value), 'Ingresos']) as any} />
                <Bar dataKey="revenue" fill="#8b5cf6" radius={[0, 4, 4, 0]} name="Ingresos" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Status pie */}
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Estado de pagos</h3>
          {statusData.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
              Sin datos
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={4}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {statusData.map((entry, index) => {
                    const colorMap: Record<string, string> = {
                      'Aprobado': '#22c55e', 'Pendiente': '#eab308', 'Cancelado': '#ef4444', 'Rechazado': '#ef4444'
                    }
                    return <Cell key={`cell-${index}`} fill={colorMap[entry.name] || COLORS[index % COLORS.length]} />
                  })}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Payment method */}
      {methodData.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Medio de pago (aprobados)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={methodData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" fontSize={12} />
              <YAxis tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} fontSize={12} />
              <Tooltip formatter={((value: any, name: any) => [
                name === 'ingresos' ? formatCLP(value) : value,
                name === 'ingresos' ? 'Ingresos' : 'Cantidad'
              ]) as any} />
              <Bar dataKey="ingresos" fill="#4f46e5" radius={[4, 4, 0, 0]} name="ingresos" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Cohort comparison table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="p-5 border-b">
          <h3 className="font-semibold text-gray-900">Comparativa por cohorte</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cohorte</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Alumnos</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pagos</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ingresos</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ticket prom.</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {cohortData.map((c, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      c.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {c.active ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">{c.students}</td>
                  <td className="px-4 py-3 text-right">{c.transactions}</td>
                  <td className="px-4 py-3 text-right font-medium">{formatCLP(c.revenue)}</td>
                  <td className="px-4 py-3 text-right">{c.transactions > 0 ? formatCLP(c.revenue / c.transactions) : '-'}</td>
                </tr>
              ))}
              {cohortData.length > 1 && (
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-4 py-3">Total</td>
                  <td className="px-4 py-3" />
                  <td className="px-4 py-3 text-right">{cohortData.reduce((s, c) => s + c.students, 0)}</td>
                  <td className="px-4 py-3 text-right">{cohortData.reduce((s, c) => s + c.transactions, 0)}</td>
                  <td className="px-4 py-3 text-right">{formatCLP(cohortData.reduce((s, c) => s + c.revenue, 0))}</td>
                  <td className="px-4 py-3 text-right">
                    {cohortData.reduce((s, c) => s + c.transactions, 0) > 0
                      ? formatCLP(cohortData.reduce((s, c) => s + c.revenue, 0) / cohortData.reduce((s, c) => s + c.transactions, 0))
                      : '-'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent transactions */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="p-5 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Ultimas transacciones</h3>
          <span className="text-xs text-gray-500">{filteredPayments.length} registros</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alumno</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cohorte</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Medio</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Monto</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredPayments.slice(0, 20).map((p) => {
                const profile = p.profile_id ? profileMap.get(p.profile_id) : null
                const cohortName = p.cohort_id ? cohortMap.get(p.cohort_id)?.name : (p.profile_id ? profileCohortMap.get(p.profile_id)?.[0] : null)
                const statusStyle = STATUS_COLORS[p.status] || { bg: 'bg-gray-100', text: 'text-gray-600', label: p.status }
                const date = p.payment_date || p.created_at

                return (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {date ? new Date(date).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                    </td>
                    <td className="px-4 py-3">
                      {profile
                        ? <span className="font-medium">{profile.first_name} {profile.last_name}</span>
                        : <span className="text-gray-400">Sin perfil</span>
                      }
                    </td>
                    <td className="px-4 py-3">
                      {cohortName || <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {p.payment_method === 'webpay_plus' ? 'Webpay' : p.payment_method === 'mercadopago' ? 'MercadoPago' : p.payment_method || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                        {statusStyle.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium whitespace-nowrap">
                      {formatCLP(Number(p.amount))}
                      {p.currency && p.currency !== 'CLP' && (
                        <span className="text-xs text-gray-400 ml-1">{p.currency}</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {filteredPayments.length > 20 && (
          <div className="p-3 text-center text-xs text-gray-500 border-t">
            Mostrando 20 de {filteredPayments.length} transacciones
          </div>
        )}
      </div>
    </div>
  )
}
